<template>
  <div :class="['vue-tel-input', wrapperClasses, { disabled: disabled }]">
    <div
      v-click-outside="clickedOutside"
      :class="['vti__dropdown', { open: open }]"
      :tabindex="dropdownOptions && dropdownOptions.tabindex ? dropdownOptions.tabindex : 0"
      @keydown="keyboardNav"
      @click="toggleDropdown"
      @keydown.esc="reset"
    >
      <span class="vti__selection">
        <div
          v-if="enabledFlags"
          :class="['vti__flag', activeCountry.iso2.toLowerCase()]"
        />
        <span
          v-if="enabledCountryCode"
          class="vti__country-code"
        >
          +{{ activeCountry.dialCode }}
        </span>
        <slot
          name="arrow-icon"
          :open="open"
        >
          <span class="vti__dropdown-arrow">{{ open ? '▲' : '▼' }}</span>
        </slot>
      </span>
      <ul
        v-show="open"
        ref="list"
        class="vti__dropdown-list"
        :class="dropdownOpenDirection"
      >
        <li
          v-for="(pb, index) in sortedCountries"
          :key="pb.iso2 + (pb.preferred ? '-preferred' : '')"
          :class="['vti__dropdown-item', getItemClass(index, pb.iso2)]"
          @click="choose(pb, true)"
          @mousemove="selectedIndex = index"
        >
          <div
            v-if="enabledFlags"
            :class="['vti__flag', pb.iso2.toLowerCase()]"
          />
          <strong>{{ pb.name }}</strong>
          <span v-if="dropdownOptions && !dropdownOptions.disabledDialCode">
            +{{ pb.dialCode }}
          </span>
        </li>
      </ul>
    </div>
    <input
      :id="inputId"
      ref="input"
      v-model="phone"
      type="tel"
      :autocomplete="autocomplete"
      :autofocus="autofocus"
      :class="['vti__input', inputClasses]"
      :disabled="disabled"
      :maxlength="maxLen"
      :name="name"
      :placeholder="parsedPlaceholder"
      :readonly="readonly"
      :required="required"
      :tabindex="inputOptions && inputOptions.tabindex ? inputOptions.tabindex : 0"
      @blur="onBlur"
      @focus="onFocus"
      @input="onInput"
      @change="onChangeInput"
      @keyup.enter="onEnter"
      @keyup.space="onSpace"
    >
  </div>
</template>

<script>
import PhoneNumber from 'awesome-phonenumber';
import countryList from './all-countries';

// Vue 3 port of vue-tel-input v4.4.2 (the v4 line is Vue 2 only). Behaviour and
// CSS class names match the original so existing templates render unchanged.
// `v-model` maps to `modelValue` / `update:modelValue` (Vue 3 convention) while
// the legacy `input` event is still emitted for any non-v-model listeners.
const clickOutside = {
  beforeMount(el, binding) {
    el.__vtiClickOutside = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event);
      }
    };
    document.addEventListener('click', el.__vtiClickOutside);
  },
  unmounted(el) {
    document.removeEventListener('click', el.__vtiClickOutside);
    delete el.__vtiClickOutside;
  },
};

function setCaretPosition(ctrl, pos) {
  if (ctrl.setSelectionRange) {
    ctrl.focus();
    ctrl.setSelectionRange(pos, pos);
  }
}

export default {
  name: 'VueTelInput',
  directives: {
    clickOutside,
  },
  props: {
    modelValue: { type: String, default: '' },
    allCountries: { type: Array, default: () => countryList },
    autocomplete: { type: String, default: 'on' },
    autofocus: { type: Boolean, default: false },
    customValidate: { type: [Boolean, RegExp], default: false },
    defaultCountry: { type: String, default: 'us' },
    disabled: { type: Boolean, default: false },
    dropdownOptions: { type: Object, default: () => ({}) },
    dynamicPlaceholder: { type: Boolean, default: false },
    enabledCountryCode: { type: Boolean, default: false },
    enabledFlags: { type: Boolean, default: true },
    ignoredCountries: { type: Array, default: () => [] },
    inputClasses: { type: [String, Array, Object], default: '' },
    inputId: { type: String, default: '' },
    inputOptions: { type: Object, default: () => ({}) },
    invalidMsg: { type: String, default: '' },
    maxLen: { type: Number, default: 25 },
    name: { type: String, default: 'telephone' },
    mode: { type: String, default: '' },
    onlyCountries: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Enter a phone number' },
    preferredCountries: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    validCharactersOnly: { type: Boolean, default: false },
    wrapperClasses: { type: [String, Array, Object], default: '' },
  },
  emits: [
    'update:modelValue', 'input', 'validate', 'onValidate', 'onInput',
    'open', 'close', 'country-changed', 'blur', 'onBlur', 'focus',
    'enter', 'onEnter', 'space', 'onSpace', 'change',
  ],
  data() {
    return {
      phone: '',
      activeCountry: { iso2: '' },
      open: false,
      finishMounted: false,
      selectedIndex: null,
      typeToFindInput: '',
      typeToFindTimer: null,
      cursorPosition: 0,
      dropdownOpenDirection: 'below',
    };
  },
  computed: {
    parsedPlaceholder() {
      if (!this.finishMounted) {
        return '';
      }
      if (this.dynamicPlaceholder) {
        const mode = this.mode || 'international';

        return PhoneNumber.getExample(this.activeCountry.iso2, 'mobile').getNumber(mode);
      }

      return this.placeholder;
    },
    parsedMode() {
      if (this.customValidate) {
        return 'input';
      }
      if (this.mode) {
        if (!['international', 'national'].includes(this.mode)) {
          console.error('Invalid value of prop "mode"');
        } else {
          return this.mode;
        }
      }
      if (!this.phone || this.phone[0] !== '+') {
        return 'national';
      }

      return 'international';
    },
    filteredCountries() {
      if (this.onlyCountries.length) {
        return this.allCountries
          .filter(({ iso2 }) => this.onlyCountries.some((c) => c.toUpperCase() === iso2));
      }
      if (this.ignoredCountries.length) {
        return this.allCountries.filter(
          ({ iso2 }) => !this.ignoredCountries.includes(iso2.toUpperCase())
            && !this.ignoredCountries.includes(iso2.toLowerCase()),
        );
      }

      return this.allCountries;
    },
    sortedCountries() {
      const preferredCountries = this.getCountries(this.preferredCountries)
        .map((country) => ({ ...country, preferred: true }));

      return [...preferredCountries, ...this.filteredCountries];
    },
    phoneObject() {
      const result = PhoneNumber(this.phone, this.activeCountry.iso2).toJSON();

      Object.assign(result, {
        isValid: result.valid,
        country: this.activeCountry,
      });

      return result;
    },
    phoneText() {
      let key = 'input';

      if (this.phoneObject.valid) {
        key = this.parsedMode;
      }

      return this.phoneObject.number[key] || '';
    },
  },
  watch: {
    'phoneObject.valid': function phoneObjectValid(value) {
      if (value) {
        this.phone = this.phoneText;
      }
      this.$emit('validate', this.phoneObject);
      this.$emit('onValidate', this.phoneObject);
    },
    modelValue() {
      this.phone = this.modelValue;
    },
    open(isDropdownOpened) {
      if (isDropdownOpened) {
        this.setDropdownPosition();
        this.$emit('open');
      } else {
        this.$emit('close');
      }
    },
    phone(newValue, oldValue) {
      const isValidCharactersOnly = this.validCharactersOnly && !this.testCharacters();
      const isCustomValidate = this.customValidate && !this.testCustomValidate();

      if (isValidCharactersOnly || isCustomValidate) {
        this.$nextTick(() => { this.phone = oldValue; });
      } else if (newValue) {
        if (newValue[0] === '+') {
          const code = PhoneNumber(newValue).getRegionCode();

          if (code) {
            this.activeCountry = this.findCountry(code) || this.activeCountry;
          }
        }
      }
      if (this.cursorPosition < oldValue.length) {
        this.$nextTick(() => { setCaretPosition(this.$refs.input, this.cursorPosition); });
      }
    },
    activeCountry(value) {
      if (value && value.iso2) {
        this.$emit('country-changed', value);
      }
    },
  },
  created() {
    if (this.modelValue) {
      this.phone = this.modelValue.trim();
    }
  },
  mounted() {
    this.initializeCountry();
    if (!this.phone
      && this.inputOptions
      && this.inputOptions.showDialCode
      && this.activeCountry.dialCode) {
      this.phone = `+${this.activeCountry.dialCode}`;
    }
    this.$emit('validate', this.phoneObject);
    this.$emit('onValidate', this.phoneObject);
    this.finishMounted = true;
  },
  methods: {
    initializeCountry() {
      if (this.phone && this.phone[0] === '+') {
        const region = PhoneNumber(this.phone).getRegionCode();

        if (region) {
          this.choose(region);

          return;
        }
      }
      if (this.defaultCountry) {
        const defaultCountry = this.findCountry(this.defaultCountry);

        if (defaultCountry) {
          this.choose(defaultCountry);

          return;
        }
      }
      // Last resort only (the `defaultCountry` prop defaults to 'us', so we
      // normally never get here). The original lib hit ip2c.org for IP-based
      // geolocation at this point; we drop that external request entirely.
      this.choose(this.findCountry(this.preferredCountries[0]) || this.filteredCountries[0]);
    },
    getCountries(list = []) {
      return list
        .map((countryCode) => this.findCountry(countryCode))
        .filter(Boolean);
    },
    findCountry(iso = '') {
      return this.filteredCountries.find((country) => country.iso2 === iso.toUpperCase());
    },
    getItemClass(index, iso2) {
      const highlighted = this.selectedIndex === index;
      const lastPreferred = index === this.preferredCountries.length - 1;
      const preferred = this.preferredCountries.some((c) => c.toUpperCase() === iso2);

      return {
        highlighted,
        'last-preferred': lastPreferred,
        preferred,
      };
    },
    choose(country, toEmitInputEvent = false) {
      let parsedCountry = country;

      if (typeof parsedCountry === 'string') {
        parsedCountry = this.findCountry(parsedCountry);
      }
      if (!parsedCountry) {
        return;
      }
      this.activeCountry = parsedCountry || this.activeCountry || {};
      if (this.phone
        && this.phone[0] === '+'
        && this.activeCountry.iso2
        && this.phoneObject.number.national) {
        this.phone = PhoneNumber(this.phoneObject.number.national, this.activeCountry.iso2)
          .getNumber('international');
      } else if (this.inputOptions && this.inputOptions.showDialCode && parsedCountry) {
        this.phone = `+${parsedCountry.dialCode}`;
      }
      if (toEmitInputEvent) {
        this.emitInput();
      }
    },
    emitInput() {
      this.$emit('update:modelValue', this.phoneText);
      this.$emit('input', this.phoneText, this.phoneObject);
      this.$emit('onInput', this.phoneObject);
    },
    testCharacters() {
      const re = /^[()\-+0-9\s]*$/;

      return re.test(this.phone);
    },
    testCustomValidate() {
      return this.customValidate instanceof RegExp ? this.customValidate.test(this.phone) : false;
    },
    onInput(e) {
      if (this.validCharactersOnly && !this.testCharacters()) {
        return;
      }
      if (this.customValidate && !this.testCustomValidate()) {
        return;
      }
      this.$refs.input.setCustomValidity(this.phoneObject.valid ? '' : this.invalidMsg);
      this.emitInput();

      if (e && e.target) {
        this.cursorPosition = e.target.selectionStart;
      }
    },
    onChangeInput() {
      this.$emit('change', this.phone, this.phoneObject);
    },
    onBlur() {
      this.$emit('blur');
      this.$emit('onBlur');
    },
    onFocus() {
      this.$emit('focus');
    },
    onEnter() {
      this.$emit('enter');
      this.$emit('onEnter');
    },
    onSpace() {
      this.$emit('space');
      this.$emit('onSpace');
    },
    focus() {
      this.$refs.input.focus();
    },
    toggleDropdown() {
      if (this.disabled) {
        return;
      }
      this.open = !this.open;
    },
    clickedOutside() {
      this.open = false;
    },
    keyboardNav(e) {
      if (e.keyCode === 40) {
        e.preventDefault();
        this.open = true;
        if (this.selectedIndex === null) {
          this.selectedIndex = 0;
        } else {
          this.selectedIndex = Math.min(this.sortedCountries.length - 1, this.selectedIndex + 1);
        }
        const selEle = this.$refs.list.children[this.selectedIndex];

        if (selEle.offsetTop + selEle.clientHeight
          > this.$refs.list.scrollTop + this.$refs.list.clientHeight) {
          this.$refs.list.scrollTop = selEle.offsetTop
            - this.$refs.list.clientHeight
            + selEle.clientHeight;
        }
      } else if (e.keyCode === 38) {
        e.preventDefault();
        this.open = true;
        if (this.selectedIndex === null) {
          this.selectedIndex = this.sortedCountries.length - 1;
        } else {
          this.selectedIndex = Math.max(0, this.selectedIndex - 1);
        }
        const selEle = this.$refs.list.children[this.selectedIndex];

        if (selEle.offsetTop < this.$refs.list.scrollTop) {
          this.$refs.list.scrollTop = selEle.offsetTop;
        }
      } else if (e.keyCode === 13) {
        if (this.selectedIndex !== null) {
          this.choose(this.sortedCountries[this.selectedIndex], true);
        }
        this.open = !this.open;
      } else {
        this.typeToFindInput += e.key;
        clearTimeout(this.typeToFindTimer);
        this.typeToFindTimer = setTimeout(() => {
          this.typeToFindInput = '';
        }, 700);
        const typedCountryI = this.sortedCountries
          .slice(this.preferredCountries.length)
          .findIndex((c) => c.name.toLowerCase().startsWith(this.typeToFindInput));

        if (typedCountryI >= 0) {
          this.selectedIndex = this.preferredCountries.length + typedCountryI;
          const selEle = this.$refs.list.children[this.selectedIndex];
          const needToScrollTop = selEle.offsetTop < this.$refs.list.scrollTop;
          const needToScrollBottom = selEle.offsetTop + selEle.clientHeight
            > this.$refs.list.scrollTop + this.$refs.list.clientHeight;

          if (needToScrollTop || needToScrollBottom) {
            this.$refs.list.scrollTop = selEle.offsetTop - this.$refs.list.clientHeight / 2;
          }
        }
      }
    },
    reset() {
      this.selectedIndex = this.sortedCountries.map((c) => c.iso2).indexOf(this.activeCountry.iso2);
      this.open = false;
    },
    setDropdownPosition() {
      const spaceBelow = window.innerHeight - this.$el.getBoundingClientRect().bottom;
      const hasEnoughSpaceBelow = spaceBelow > 200;

      this.dropdownOpenDirection = hasEnoughSpaceBelow ? 'below' : 'above';
    },
  },
};
</script>

<style src="./sprite.css"></style>
<style>
.vue-tel-input {
  border-radius: 3px;
  display: flex;
  border: 1px solid #bbb;
  text-align: left;
}
.vue-tel-input.disabled .selection,
.vue-tel-input.disabled .dropdown,
.vue-tel-input.disabled input {
  cursor: no-drop;
}
.vue-tel-input:focus-within {
  box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 8px rgba(102, 175, 233, 0.6);
  border-color: #66afe9;
}
.vti__dropdown {
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: center;
  position: relative;
  padding: 7px;
  cursor: pointer;
}
.vti__dropdown.open {
  background-color: #f3f3f3;
}
.vti__dropdown:hover {
  background-color: #f3f3f3;
}
.vti__selection {
  font-size: 0.8em;
  display: flex;
  align-items: center;
}
.vti__selection .vti__country-code {
  color: #666;
}
.vti__flag {
  margin-right: 5px;
  margin-left: 5px;
}
.vti__dropdown-list {
  z-index: 1;
  padding: 0;
  margin: 0;
  text-align: left;
  list-style: none;
  max-height: 200px;
  overflow-y: scroll;
  position: absolute;
  left: -1px;
  background-color: #fff;
  border: 1px solid #ccc;
  width: 390px;
}
.vti__dropdown-list.below {
  top: 33px;
}
.vti__dropdown-list.above {
  top: auto;
  bottom: 100%;
}
.vti__dropdown-arrow {
  transform: scaleY(0.5);
  display: inline-block;
  color: #666;
}
.vti__dropdown-item {
  cursor: pointer;
  padding: 4px 15px;
}
.vti__dropdown-item.highlighted {
  background-color: #f3f3f3;
}
.vti__dropdown-item.last-preferred {
  border-bottom: 1px solid #cacaca;
}
.vti__dropdown-item .vti__flag {
  display: inline-block;
  margin-right: 5px;
}
.vti__input {
  border: none;
  border-radius: 0 2px 2px 0;
  width: 100%;
  outline: none;
  padding-left: 7px;
}
</style>
